import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Heart,
  Trophy,
  Smile,
  Sparkles,
  Quote,
  HandHeart,
  ShieldCheck,
  Dumbbell,
  PartyPopper,
  MessageCircle,
} from "lucide-react";

const heroImg =
  "https://images.pexels.com/photos/1607855/pexels-photo-1607855.jpeg";
const galleryImgs = [
  "https://images.pexels.com/photos/12765717/pexels-photo-12765717.jpeg",
  "https://images.pexels.com/photos/15476801/pexels-photo-15476801.jpeg",
  "https://images.pexels.com/photos/5407033/pexels-photo-5407033.jpeg",
  "https://images.pexels.com/photos/13965302/pexels-photo-13965302.jpeg",
];

const pillars = [
  {
    icon: Users,
    title: "Inclusion First",
    text: "Every kid belongs on the court. No tryouts, no cuts — just a team.",
  },
  {
    icon: Trophy,
    title: "Real Teamwork",
    text: "Players learn the game alongside student volunteers who cheer them on.",
  },
  {
    icon: Smile,
    title: "All About Fun",
    text: "Laughter, high-fives and confidence-building drills every single week.",
  },
];

const testimonials = [
  {
    quote:
      "Every kid deserves a place to feel included and part of a team.",
  },
  {
    quote:
      "We're building a community where players can grow, have fun, and be themselves.",
  },
  {
    quote:
      "UHL is about confidence, teamwork, and creating opportunities for everyone.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-extrabold uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              Founded and run by student volunteers
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
              Unified <span className="text-blue-600">Hoops</span>{" "}
              <span className="text-orange-500">League</span>{" "}
              <span className="text-slate-900">(UHL)</span>
            </h1>
            <p className="mt-5 text-xl sm:text-2xl font-bold text-slate-800 max-w-xl leading-snug">
              A free basketball program where every player belongs —
              building confidence, friendships, and joy through the game.
            </p>
            <p className="mt-4 text-base sm:text-lg font-medium leading-relaxed text-slate-600 max-w-xl">
              UHL is an inclusive program for kids with intellectual and
              developmental disabilities, founded and run by high school
              student volunteers.
            </p>

            <div
              data-testid="launch-banner"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-200 px-4 py-2 text-orange-700 text-sm font-extrabold"
            >
              <Sparkles className="w-4 h-4" />
              Launching soon — be the first to hear when registration opens.
            </div>

            <div
              data-testid="hero-trust-line"
              className="mt-6 flex items-start gap-3 rounded-2xl bg-blue-50/70 border border-blue-100 px-5 py-4 max-w-xl"
            >
              <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                Built with guidance from experienced volunteers and community
                programs to ensure a safe, supportive, and positive
                experience for every player.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Join%20Interest%20List%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20be%20added%20to%20your%20interest%20list.%20Please%20send%20me%20updates%20when%20registration%20opens.%0A%0AName%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AAny%20questions%3A%20"
                data-testid="hero-register-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 hover:shadow-xl hover:-translate-y-0.5 transition-all font-bold px-8 py-4 rounded-full text-lg"
              >
                📩 Join Interest List <ArrowRight className="w-5 h-5" />
              </a>
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-all font-bold px-8 py-4 rounded-full text-lg"
              >
                Our Story
              </Link>
            </div>

            <div className="mt-10 space-y-2 max-w-xl">
              <p className="text-base font-bold text-slate-800">
                UHL is completely free for every family.
              </p>
              <p className="text-sm font-medium text-slate-600">
                Jerseys, equipment, and coaching are all included.
              </p>
            </div>
          </div>

          <div className="relative fade-up">
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-orange-500/20 blur-2xl" />
            <div className="absolute -bottom-8 -right-4 w-52 h-52 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/10 border-[6px] border-white">
              <img
                src={heroImg}
                alt="Outdoor basketball court"
                className="w-full h-[420px] sm:h-[520px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-xl shadow-blue-900/10 px-5 py-4 flex items-center gap-3 border border-slate-100">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <HandHeart className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-black text-slate-900 text-sm">
                  A jersey, a team, a community
                </p>
                <p className="text-xs text-slate-500 font-semibold">
                  A supportive environment to learn and have fun
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUPPORT UHL BANNER */}
      <section className="pt-10 pb-2" data-testid="home-support-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 to-orange-600 p-8 md:p-10 text-white shadow-xl shadow-orange-500/20">
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-100">
                  Support UHL
                </p>
                <h2 className="mt-2 text-2xl sm:text-3xl font-black leading-tight">
                  Help keep basketball free for every family.
                </h2>
                <p className="mt-2 text-orange-50 font-medium max-w-xl">
                  Donations fund jerseys, equipment, and gym time — and go
                  directly to the kids on our court.
                </p>
              </div>
              <a
                href="https://www.zeffy.com/donation-form/support-unified-hoops-league-uhl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-donate-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 hover:bg-orange-50 hover:-translate-y-0.5 hover:shadow-2xl transition-all font-black px-8 py-4 rounded-full text-lg whitespace-nowrap"
              >
                <Heart className="w-5 h-5" /> Donate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-16 md:py-24 bg-white" data-testid="who-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              Who this is for
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Built for kids who love the game — exactly as they are.
            </h2>
            <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600">
              UHL is designed for athletes with intellectual and
              developmental disabilities. Whether your child is new to
              basketball or already loves the game, there's a place for them
              on our court.
            </p>
            <p className="mt-4 text-lg font-medium leading-relaxed text-slate-600">
              Open to a wide range of ages and experience levels — players
              are grouped by ability and comfort level to ensure a great
              experience for everyone.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {pillars.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`pillar-${title.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-white rounded-3xl p-8 shadow-xl shadow-blue-900/5 border border-slate-100 hover:shadow-2xl hover:shadow-blue-900/10 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="mt-5 text-xl font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section
        className="py-16 md:py-24 bg-white border-t border-slate-100"
        data-testid="what-to-expect-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
              What to Expect
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              A simple, supportive experience for every player.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Heart,
                title: "Supportive & inclusive",
                text: "A welcoming environment where every player feels seen and celebrated.",
              },
              {
                icon: Smile,
                title: "Fun over competition",
                text: "We focus on confidence, friendship, and joy — not the scoreboard.",
              },
              {
                icon: Sparkles,
                title: "No experience required",
                text: "Brand new to the game? Perfect. We meet players where they are.",
              },
              {
                icon: HandHeart,
                title: "Players supported at their own pace",
                text: "Student volunteers help every player learn and grow, step by step.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`expect-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-[#FAFAFA] rounded-3xl p-7 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="mt-5 text-lg font-black text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed text-sm">
                  {text}
                </p>
              </div>
            ))}
          </div>

          <p
            data-testid="program-details-note"
            className="mt-10 text-center text-sm font-semibold text-slate-500 italic"
          >
            Program details — including schedule and location — will be
            shared after launch.
          </p>

          {/* PAASS PARTNERSHIP BANNER */}
          <div
            data-testid="paass-partnership"
            className="mt-12 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-blue-900/5 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ubl-hoops/artifacts/k2t3nrpa_Screenshot%202026-05-14%20at%2011.07.22%E2%80%AFAM.png"
              alt="PAASS — Project Awareness and Special Sports"
              className="w-24 h-24 sm:w-28 sm:h-28 object-contain flex-shrink-0"
            />
            <div className="text-center sm:text-left flex-1">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
                Officially Partnered With
              </p>
              <h3 className="mt-1 text-xl sm:text-2xl font-black text-slate-900">
                PAASS — Project Awareness and Special Sports
              </h3>
              <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                UHL is officially partnered with PAASS, a community
                organization dedicated to creating inclusive sports
                experiences for kids with intellectual and developmental
                disabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="py-16 md:py-24 bg-[#FAFAFA]"
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
              How UHL Works
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Structured. Supportive. Always fun.
            </h2>
            <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600">
              Each session includes a mix of warm-ups, skill-building drills,
              and fun, game-style play. Every player is supported by student
              volunteers who help create a positive and encouraging
              experience.
            </p>
            <p className="mt-4 text-lg font-semibold leading-relaxed text-slate-800">
              Our focus is not competition — it's confidence, teamwork, and
              having fun.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Dumbbell,
                title: "Warm-Ups",
                text: "Fun, movement-based activities that get everyone moving and smiling.",
              },
              {
                icon: Trophy,
                title: "Skill-Building Drills",
                text: "Dribbling, passing, and shooting — adapted to each player's pace with support from student volunteers.",
              },
              {
                icon: PartyPopper,
                title: "Fun, Game-Style Play",
                text: "Small scrimmages where every kid plays, gets the ball, and celebrates together.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`how-it-works-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl shadow-blue-900/5 hover:-translate-y-1 hover:shadow-2xl transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="mt-5 text-xl font-black text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 rounded-2xl bg-white border border-slate-100 px-5 py-4 shadow-sm">
              <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                All sessions are supervised, and volunteers work closely
                with players to ensure a safe and supportive environment.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-2xl bg-white border border-slate-100 px-5 py-4 shadow-sm">
              <Sparkles className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                UHL is launching with small group sessions to ensure every
                player gets the attention and support they need, with plans
                to grow into a larger league.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        id="gallery"
        data-testid="home-gallery"
        className="py-16 md:py-24 bg-[#FAFAFA]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
                On our courts
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Where the game happens.
              </h2>
            </div>
            <p className="max-w-sm text-slate-600 font-medium">
              Real photos from our program will be added as we launch.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {galleryImgs.map((src, i) => (
              <div
                key={i}
                className={`rounded-3xl overflow-hidden shadow-lg shadow-blue-900/5 border border-slate-100 ${
                  i === 0 ? "col-span-2 row-span-2" : ""
                }`}
              >
                <img
                  src={src}
                  alt={`UHL gallery ${i + 1}`}
                  className={`w-full object-cover hover:scale-105 transition-transform duration-700 ${
                    i === 0 ? "h-full min-h-[280px]" : "h-40 md:h-48"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              Our Values
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              What UHL is all about.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-[#FAFAFA] rounded-3xl p-8 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all"
              >
                <Quote className="w-8 h-8 text-orange-500" />
                <p className="mt-4 text-slate-800 font-semibold leading-relaxed text-lg">
                  "{t.quote}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl bg-blue-600 px-8 py-14 sm:px-14 sm:py-20">
            <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-orange-500/30 blur-3xl" />
            <div className="absolute bottom-0 -left-10 w-52 h-52 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-extrabold uppercase tracking-wider">
                <Sparkles className="w-4 h-4" />
                Launching soon
              </div>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
                Be the first to hear when we launch.
              </h2>
              <p className="mt-4 text-lg text-blue-50 font-medium">
                Registration opens soon. Join our interest list to reserve
                your spot or hear updates as we get closer to our first
                session.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="mailto:unifiedhoopsleague@gmail.com?subject=Reserve%20My%20Spot%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20reserve%20a%20spot%20for%20my%20player.%20Please%20follow%20up%20with%20next%20steps%20when%20registration%20opens.%0A%0AParent%20name%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AAny%20notes%3A%20"
                  data-testid="cta-register-btn"
                  className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 hover:shadow-xl hover:-translate-y-0.5 transition-all font-bold px-8 py-4 rounded-full text-lg"
                >
                  📩 Reserve Your Spot <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="mailto:unifiedhoopsleague@gmail.com?subject=Volunteer%20Early%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20volunteer.%20Please%20share%20more%20about%20what%27s%20involved%20and%20how%20I%20can%20help.%0A%0AName%3A%20%0ASchool%2Fgrade%3A%20%0AAvailability%3A%20"
                  data-testid="cta-volunteer-btn"
                  className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 transition-all font-bold px-8 py-4 rounded-full text-lg"
                >
                  📩 Volunteer Early
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FRIENDLY CLOSER */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="home-closer"
            className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center"
          >
            <MessageCircle className="w-5 h-5 text-orange-500" />
            <p className="text-slate-700 font-semibold">
              Questions? Reach out anytime —{" "}
              <Link
                to="/contact"
                data-testid="home-closer-link"
                className="text-blue-600 underline underline-offset-4 hover:text-blue-700"
              >
                we're happy to help.
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
